/* eslint-disable camelcase */

import { Tooltip, Typography } from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import ButtonComp from 'common/components/Button/ButtonComp';
import ModalEle from 'common/components/Modal/ModalEle';
import { Column } from 'common/components/Table/CommonTableStyles';
import TableComponent from 'common/components/Table/Table';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import {
	setExistingPromptDetails,
	setIsPromptEdit,
	setIsPromptModalOpen,
	setIsReloadTableData,
	setSelectedPrompt,
} from 'common/store/reducer/promptBuilderReducer';
import { truncateString } from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import { ITablePaginationParams } from 'features/RegulationInventory/api/type-def';

import { getPromptTableData } from './api';
import { IPromptBuilderParams } from './api/type-def';
import PromptClone from './PromptActions/PromptClone/PromptClone';
import PromptDefault from './PromptActions/PromptDefault/PromptDefault';
import PromptDelete from './PromptActions/PromptDelete/PromptDelete';
import PromptTableMenu from './PromptTableMenu/PromptTableMenu';
import PromptTags from './PromptTags/PromptTags';

import promptBuilderStyles from './PromptBuilderLayout.module.scss';

interface IPromptBuilderPromptsList {
	reload?: boolean;
	resetHandler?: () => void;
}

const { OK } = HTTP_STATUS_CODES;

const PromptBuilderPromptsList = (props: IPromptBuilderPromptsList) => {
	const { resetHandler } = props;
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(false);
	const [tableData, setTableData] = useState<IPromptBuilderParams[]>([]);
	const [totalRecordsCount, setTotalRecordCount] = useState<number>(0);
	const { tableQueryObj, updateTableQuery } = useTableQuery({
		id: 'promptLibTable',
		defaultPageSize: 10,
	});
	const [showCloneModal, setShowCloneModal] = useState<boolean>(false);
	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
	const [showDefaultModal, setShowDefaultModal] = useState<boolean>(false);

	const reloadTableData: boolean = useSelector(
		(state: RootState) => state.promptBuilder.reloadTableData,
	);

	const promptSearchText = useSelector(
		(state: RootState) => state.common.promptSearchText,
	);

	const getPromptsTableData = async (data: ITablePaginationParams) => {
		setIsLoading(true);
		const response = await getPromptTableData(data, promptSearchText);
		if (response?.responseBody && response?.statusCode === OK) {
			const responseData = response?.responseBody;
			setTableData(responseData?.data);
			setTotalRecordCount(responseData?.total);
			setIsLoading(false);
		} else {
			dispatch(
				setToastMessage({
					message: APP_GENERAL_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
			setIsLoading(false);
		}
		dispatch(setIsReloadTableData(false));
	};
	useEffect(() => {
		getPromptsTableData(tableQueryObj);
	}, []);

	useEffect(() => {
		getPromptsTableData(tableQueryObj);
	}, [promptSearchText]);

	useEffect(() => {
		if (reloadTableData) {
			getPromptsTableData(tableQueryObj);
			if (resetHandler) {
				resetHandler();
			}
		}
	}, [reloadTableData]);

	const handlePageChange = (data: any, flag?: boolean) => {
		if (flag) {
			updateTableQuery(data);
			getPromptsTableData(data);
		}
	};

	const handleSortChange = (data: any) => {
		updateTableQuery(data);
		getPromptsTableData(data);
	};

	const onClickEdit = (row: any) => {
		dispatch(setIsPromptModalOpen(true));
		dispatch(setIsPromptEdit(true));
		const updatedRow = { ...row };
		const parameters = { ...updatedRow?.parameters };
		if (!parameters?.industry) {
			parameters.industry = '';
		}

		if (!parameters?.category) {
			parameters.category = '';
		}
		updatedRow.parameters = parameters;
		updatedRow.aiModelKey = row.ai_model_key || '';
		updatedRow.is_active = row.is_active;
		dispatch(setExistingPromptDetails(updatedRow));
	};

	const handleClone = (params: IPromptBuilderParams) => {
		dispatch(setSelectedPrompt(params));
		setShowCloneModal(true);
	};

	const handleDelete = (params: IPromptBuilderParams) => {
		dispatch(setSelectedPrompt(params));
		setShowDeleteModal(true);
	};

	const editHandler = (params: IPromptBuilderParams) => {
		dispatch(setSelectedPrompt(params));
		onClickEdit(params);
	};

	const defaultHandler = (params: IPromptBuilderParams) => {
		dispatch(setSelectedPrompt(params));
		setShowDefaultModal(true);
	};

	const modalStyle = { width: '30%', height: '30%', paddingLeft: 0 };

	const columns: Column[] = [
		{
			id: 'key',
			label: 'Title',
			disablePadding: false,
			isSortable: true,
			render: (params: IPromptBuilderParams) => (
				<Typography
					className={promptBuilderStyles['table-title']}
					data-testid="prompt-tooltipkey"
				>
					{params?.key.length > 25 ? (
						<Tooltip title={params?.key}>
							<span data-testid="tooltip-key-value">{`${truncateString(
								params?.key,
								25,
							)}...`}</span>
						</Tooltip>
					) : (
						params?.key
					)}
				</Typography>
			),
		},
		{
			id: 'ai_model_name',
			label: 'Prompt output',
			disablePadding: false,
			hideSentenceCase: true,
			isSortable: true,
			render: (params: IPromptBuilderParams) => (
				<Typography className={promptBuilderStyles['table-description']}>
					{params?.ai_model_name?.length > 35 ? (
						<Tooltip title={params.ai_model_name}>
							<span>{`${truncateString(params.ai_model_name, 35)}...`}</span>
						</Tooltip>
					) : (
						params.ai_model_name
					)}
				</Typography>
			),
		},
		{
			id: 'tags',
			label: 'Tags',
			disablePadding: false,
			isSortable: false,
			render: (params: IPromptBuilderParams) => (
				<PromptTags promptData={params} />
			),
		},
		{
			id: 'id',
			label: '',
			disablePadding: false,
			hideSentenceCase: true,
			isSortable: false,
			render: (params: IPromptBuilderParams) => (
				<ButtonComp
					btnType="text"
					disabled={params.is_active || params.is_draft}
					compClass={promptBuilderStyles['prompt-set-as-default']}
					clickHandler={() => defaultHandler(params)}
					data-testId="default-btn"
				>
					Set as default
				</ButtonComp>
			),
		},
		{
			id: 'actions',
			label: '',
			disablePadding: false,
			isSortable: false,
			render: (params: IPromptBuilderParams) => (
				<PromptTableMenu
					isEditable={params.is_draft}
					isDeleteDisabled={params.is_active || params.is_default}
					handleClone={() => handleClone(params)}
					handleEdit={() => editHandler(params)}
					handleDelete={() => handleDelete(params)}
				/>
			),
		},
	];

	return (
		<div className={promptBuilderStyles['layout-styles']}>
			<TableComponent
				defaultQuery={tableQueryObj}
				rows={tableData}
				columns={columns}
				isLoading={isLoading}
				isRenderPagination
				enableHeaderColor
				totalItems={totalRecordsCount}
				onSortHandler={handleSortChange}
				onPageHandler={handlePageChange}
			/>
			<ModalEle open={showCloneModal} sxBoxProps={modalStyle}>
				<PromptClone closeCloneModal={() => setShowCloneModal(false)} />
			</ModalEle>
			<ModalEle open={showDeleteModal} sxBoxProps={modalStyle}>
				<PromptDelete closeDeleteModal={() => setShowDeleteModal(false)} />
			</ModalEle>
			<ModalEle
				open={showDefaultModal}
				sxBoxProps={{ width: '60%', paddingLeft: 0 }}
			>
				<PromptDefault closeDefaultModal={() => setShowDefaultModal(false)} />
			</ModalEle>
		</div>
	);
};

export default PromptBuilderPromptsList;
